import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  TableFilterState,
  TableSortingState,
} from '@ibsys/kit';
import type { TableSettings } from '../types';

/** Состояние колонок DataGrid: то, что таблица получает из своих настроек и пишет в них обратно */
export type DataGridColumnStateProps = {
  columnOrder: DataGridColumnOrderState;
  onColumnOrderChange: (next: DataGridColumnOrderState) => void;
  columnVisibility: DataGridColumnVisibilityState;
  onColumnVisibilityChange: (next: DataGridColumnVisibilityState) => void;
  columnPinning: DataGridColumnPinningState;
  onColumnPinningChange: (next: DataGridColumnPinningState) => void;
  /** Ширина колонок; записи нет — колонка в системной ширине */
  columnSizing: DataGridColumnSizingState;
  /** Изменение ширины: грид зовёт по окончании перетаскивания края, а не на каждый пиксель */
  onColumnSizingChange: (next: DataGridColumnSizingState) => void;
};

/** Часть настроек таблицы, описывающая вид колонок: её читает состояние колонок и её же пишет обратно */
export type DataGridColumnSettings = Pick<
  TableSettings,
  'columnOrder' | 'columnVisibility' | 'columnPinning' | 'columnSizing'
>;

/** Экшены настроек таблицы, нужные состоянию колонок */
export type DataGridColumnActions = {
  onFiltersChange: (filters: TableFilterState) => void;
  onSortingChange: (sorting: TableSortingState) => void;
  setColumnState: (state: DataGridColumnSettings) => void;
};

/**
 * Сохранённый состав колонок, приведённый к текущему: колонки, которых на момент сохранения ещё
 * не было, берут видимость по умолчанию — иначе появившаяся позже скрытая колонка открывалась бы
 * у всех, кто список уже настраивал под себя.
 *
 * Видимость возвращается явной по каждой известной колонке: у грида колонка без записи считается
 * видимой, поэтому скрытую по умолчанию колонку нельзя оставить без значения.
 */
export const restoreColumnVisibility = (
  saved: DataGridColumnVisibilityState,
  defaultVisibility: DataGridColumnVisibilityState,
): DataGridColumnVisibilityState => {
  const restored: DataGridColumnVisibilityState = { ...defaultVisibility };
  for (const columnId of Object.keys(defaultVisibility)) {
    if (saved[columnId] !== undefined) restored[columnId] = saved[columnId];
  }
  return restored;
};

/**
 * Одинаков ли состав видимых колонок. Сравнение по смыслу «колонка видима», а не по записям:
 * грид нормализует состояние, убирая явные `true`, а колонка без записи считается видимой,
 * поэтому один и тот же состав описывается разными наборами записей.
 */
export const isSameColumnVisibility = (a: DataGridColumnVisibilityState, b: DataGridColumnVisibilityState): boolean => {
  for (const columnId of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if ((a[columnId] !== false) !== (b[columnId] !== false)) return false;
  }
  return true;
};

/** Одинаков ли порядок колонок */
export const isSameColumnOrder = (a: DataGridColumnOrderState, b: DataGridColumnOrderState): boolean =>
  a.length === b.length && a.every((columnId, index) => columnId === b[index]);

/** Одинаково ли закрепление колонок */
export const isSameColumnPinning = (a: DataGridColumnPinningState, b: DataGridColumnPinningState): boolean =>
  isSameColumnOrder(a.left ?? [], b.left ?? []) && isSameColumnOrder(a.right ?? [], b.right ?? []);

/**
 * Сохранённый порядок колонок, приведённый к текущему: колонка, появившаяся после сохранения,
 * встаёт на своё место по умолчанию, а не в хвост списка. Настройка колонки, которой в списке
 * больше нет, порядок не меняет — грид её игнорирует.
 *
 * Пустой порядок по умолчанию означает, что состав колонок известен только из данных
 * (атрибуты, заключения) — тогда сохранённый порядок применяется как есть.
 */
export const restoreColumnOrder = (
  saved: DataGridColumnOrderState,
  defaultOrder: DataGridColumnOrderState,
): DataGridColumnOrderState => {
  const restored = [...saved];

  defaultOrder.forEach((columnId, index) => {
    if (restored.includes(columnId)) return;
    const previous = defaultOrder
      .slice(0, index)
      .reverse()
      .find((candidate) => restored.includes(candidate));
    restored.splice(previous ? restored.indexOf(previous) + 1 : 0, 0, columnId);
  });

  return restored;
};
